import { useState } from "react";

const questionTemplates = {
    1: { questionNumber: "", answers: [""], correct_answers: [""] },
    2: { questionNumber: "", question: "", answers: [""], correctAnswer: [""] },
    3: { questionNumber: "", question: "", answers: [""], correct_answer: "" },
    4: { questionNumber: "", image: "", rows: [""], columns: [""], correctAnswer: [""] },
    6: { instruction: "" },
    7: { questionNumber: "", answers: [[""]], correct_answers: [""] },
    8: { questionNumber: "", answers: [""], words: [""], correct_answers: [""] },
};

const skills = ["reading", "listening", "writing"];

export default function ExamEditor({ base_url }) {
    const [exam, setExam] = useState({ id: "", skills: {} });

    const handleExamIdChange = (e) => {
        setExam((prev) => ({ ...prev, id: e.target.value }));
    };

    const addSkill = (skill) => {
        setExam((prev) => ({
            ...prev,
            skills: {
                ...prev.skills,
                [skill]: prev.skills[skill] || [],
            },
        }));
    };

    const addPart = (skill) => {
        setExam((prev) => ({
            ...prev,
            skills: {
                ...prev.skills,
                [skill]: [
                    ...prev.skills[skill],
                    {
                        partNumber: prev.skills[skill].length + 1,
                        question_text: "",
                        instruction: "",
                        image: "",
                        paragraph: (skill === "reading" || skill === "writing") ? "" : undefined,
                        audio_link: skill === "listening" ? "" : undefined,
                        questions: []
                    },
                ],
            },
        }));
    };

    const updatePart = (skill, partIndex, key, value) => {
        const updatedParts = [...exam.skills[skill]];
        let parsedValue = value;

        if (Array.isArray(updatedParts[partIndex][key])) {
            try {
                parsedValue = JSON.parse(value);
                if (!Array.isArray(parsedValue)) throw new Error();
            } catch {
                parsedValue = value.trim() ? value.split(",").map((item) => item.trim()) : [];
            }
        }

        updatedParts[partIndex][key] = parsedValue;
        setExam({ ...exam, skills: { ...exam.skills, [skill]: updatedParts } });
    };



    const addQuestion = (skill, partIndex, type) => {
        if (!type) return;
        const updatedParts = [...exam.skills[skill]];
        updatedParts[partIndex].questions.push({
            type: Number(type),
            ...questionTemplates[type],
        });
        setExam({ ...exam, skills: { ...exam.skills, [skill]: updatedParts } });
    };

    const updateQuestion = (skill, partIndex, questionIndex, key, value) => {
        const updatedParts = [...exam.skills[skill]];
        let parsedValue = value;

        if (Array.isArray(updatedParts[partIndex].questions[questionIndex][key])) {
            try {
                // Only parse if it's valid JSON
                parsedValue = JSON.parse(value);
            } catch (e) {
                // Keep raw input if not valid JSON (prevents unwanted changes)
                parsedValue = value;
            }
        }

        updatedParts[partIndex].questions[questionIndex][key] = parsedValue;
        setExam({ ...exam, skills: { ...exam.skills, [skill]: updatedParts } });
    };

    const downloadExam = () => {
        if (!exam.id.trim()) {
            alert("Please enter a valid Exam ID.");
            return;
        }

        // Extract and restructure data
        const { id, skills } = exam;
        const finalExam = { id };

        // Recursive function to process all values
        const parseValuesRecursively = (value) => {
            if (typeof value === "string") {
                try {
                    // Attempt to parse if it's a JSON array (handles nested lists automatically)
                    const parsed = JSON.parse(value);

                    if (Array.isArray(parsed)) {
                        return parsed.map(parseValuesRecursively); // Recursively process arrays
                    }
                    return parsed;
                } catch (e) {
                    return value; // Return original string if parsing fails
                }
            } else if (Array.isArray(value)) {
                return value.map(parseValuesRecursively); // Recursively process arrays
            } else if (typeof value === "object" && value !== null) {
                return Object.fromEntries(
                    Object.entries(value).map(([key, val]) => [key, parseValuesRecursively(val)])
                );
            }
            return value;
        };


        // Bring skill categories to the top level and process recursively
        Object.entries(skills).forEach(([skill, parts]) => {
            finalExam[skill] = parts.map(({ partNumber, ...rest }) => ({
                part: partNumber,
                ...parseValuesRecursively(rest), // Ensure all nested values are processed
            }));
        });

        // Convert to JSON and download
        const json = JSON.stringify(finalExam, null, 2);
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Exam.json";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const questionTypeNames = {
        1: "Fill Missing",
        2: "Multiple Answers",
        3: "Single Answer",
        4: "Map Row Column",
        6: "Question Detail Instruction",
        7: "Fill Missing in Table",
        8: "Fill Missing by Drag and Drop",
    };

    const uploadExam = async (event) => {
        if (!exam.id.trim()) {
            alert("Please enter a valid Exam ID.");
            return;
        }
        const files = event.target.files;
        if (!files.length) return;

        try {
            const formData = new FormData();

            // Append all files to the FormData
            for (const file of files) {
                formData.append("files", file, file.webkitRelativePath);
                // `webkitRelativePath` preserves folder structure
            }

            const response = await fetch(`${base_url}/upload/${exam.id.trim()}`, {
                method: "POST",
                body: formData,
            });

            if (response.ok) {
                alert("Folder uploaded successfully");
            } else {
                alert("Upload failed");
            }
        } catch (error) {
            console.error("Upload error:", error);
            alert("An error occurred while uploading");
        }
    };


    return (
        <div className="max-w-3xl mx-auto p-6 bg-gray-100 min-h-screen relative">
            <h1 className="text-2xl font-bold mb-4">IELTS Exam Editor</h1>

            {/* Exam ID Input */}
            <div className="mb-4 p-3 bg-white shadow-md rounded-md">
                <label className="block text-lg font-semibold mb-2">Exam ID:</label>
                <input
                    type="text"
                    value={exam.id}
                    onChange={handleExamIdChange}
                    placeholder="Enter Exam ID"
                    className="w-full p-2 border rounded-md"
                />
            </div>

            {/* Add Skill Buttons */}
            <div className="flex space-x-2 mb-4">
                {skills.map((skill) => (
                    <button
                        key={skill}
                        onClick={() => addSkill(skill)}
                        className="bg-blue-500 text-white px-4 py-2 rounded-md"
                    >
                        Add {skill}
                    </button>
                ))}
            </div>

            {/* Display Skills */}
            {Object.keys(exam.skills).map((skill) => (
                <div key={skill} className="p-4 bg-white rounded-md shadow-md mb-6">
                    <h2 className="text-xl font-semibold">{skill}</h2>
                    <button
                        onClick={() => addPart(skill)}
                        className="mt-2 bg-green-500 text-white px-3 py-1 rounded-md"
                    >
                        Add Part
                    </button>

                    {exam.skills[skill].map((part, partIndex) => (
                        <div key={partIndex} className="mt-4 p-3 border rounded-md bg-gray-50">
                            <h3 className="font-semibold text-lg">Part {part.partNumber}</h3>

                            {/* Fields */}
                            <div className="mt-2">
                                <label className="block text-sm font-semibold">Question Text</label>
                                <textarea
                                    className="w-full p-2 border rounded-md"
                                    value={part.question_text}
                                    onChange={(e) => updatePart(skill, partIndex, "question_text", e.target.value)}
                                    placeholder="Enter question text"
                                />
                            </div>

                            <div className="mt-2">
                                <label className="block text-sm font-semibold">Instruction</label>
                                <textarea
                                    className="w-full p-2 border rounded-md"
                                    value={part.instruction}
                                    onChange={(e) => updatePart(skill, partIndex, "instruction", e.target.value)}
                                    placeholder="Enter instructions"
                                />
                            </div>

                            <div className="mt-2">
                                <label className="block text-sm font-semibold">Image</label>
                                <input
                                    type="text"
                                    className="w-full p-2 border rounded-md"
                                    value={part.image}
                                    onChange={(e) => updatePart(skill, partIndex, "image", e.target.value)}
                                    placeholder="Enter image URL"
                                />
                            </div>

                            {(skill === "reading" || skill === "writing") && (
                                <div className="mt-2">
                                    <label className="block text-sm font-semibold">Paragraph</label>
                                    <textarea
                                        className="w-full p-2 border rounded-md"
                                        value={part.paragraph}
                                        onChange={(e) => updatePart(skill, partIndex, "paragraph", e.target.value)}
                                        placeholder="Enter paragraph text"
                                    />
                                </div>
                            )}

                            {skill === "listening" && (
                                <div className="mt-2">
                                    <label className="block text-sm font-semibold">Audio Link</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border rounded-md"
                                        value={part.audio_link}
                                        onChange={(e) => updatePart(skill, partIndex, "audio_link", e.target.value)}
                                        placeholder="Enter audio link"
                                    />
                                </div>
                            )}

                            {/* Question Adding Section */}
                            {skill !== "writing" && (
                                <div className="mt-2">
                                    <label className="block text-sm font-semibold">Add Question</label>
                                    <select
                                        className="w-full p-2 border rounded-md"
                                        onChange={(e) => addQuestion(skill, partIndex, e.target.value)}
                                    >
                                        <option value="">Select question type</option>
                                        {Object.entries(questionTypeNames).map(([type, name]) => (
                                            <option key={type} value={type}>
                                                {name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* List of Questions */}
                            {skill !== "writing" && (part.questions.map((q, questionIndex) => (
                                <div key={questionIndex} className="mt-2 p-2 border rounded-md bg-gray-200">
                                    <h4 className="text-md font-semibold">Question Type {q.type}</h4>
                                    {Object.entries(q).map(([key, value]) =>
                                        key !== "type" ? (
                                            <div key={key} className="mt-1">
                                                <label className="block text-sm font-semibold">{key}</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-1 border rounded-md"
                                                    value={Array.isArray(value) ? value.join(", ") : value}
                                                    onChange={(e) =>
                                                        updateQuestion(skill, partIndex, questionIndex, key, e.target.value)
                                                    }
                                                />
                                            </div>
                                        ) : null
                                    )}
                                </div>
                            )))}
                        </div>
                    ))}
                </div>
            ))}

            {/* Download Button */}
            <button
                onClick={downloadExam}
                className="fixed bottom-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-md shadow-lg z-50"
            >
                Create Exam (Download JSON)
            </button>

            {/* Upload Button */}
            <label className="fixed bottom-16 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg z-50 cursor-pointer">
                Select Folder & Upload
                <input
                    type="file"
                    className="hidden"
                    webkitdirectory="true"
                    directory="true"
                    multiple
                    onChange={uploadExam}
                />
            </label>
        </div>
    );
}